<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" :title="computedTitle" @show="onShow"
                   @hidden="onHidden" :content-in-form="false" :show-confirmation="showConfirmation"
                   :is-loading="loading" scrollable modal-class="mobile-full-screen">

      <b-form @submit.prevent="onSubmit">
        <div class="row mb-2">
          <div class="col-lg-3 col-sm-4 col-xl-2">
            <template v-if="customer">
              <b-form-group>
                <label>{{ $t('Customer') }}</label>
                <b-input :value="`${customer.display_name} - ${customer.email}`" disabled/>
              </b-form-group>
            </template>
            <template v-else>
              <customer-select-input-widget
                :model="model" attribute="customer_uuid"
                expand="addresses.full_address_with_geographic_structure,shipping_address.country,billing_address.country,identification,term"
                @select="onCustomerSelect" @unselect="onCustomerUnselect">
              </customer-select-input-widget>
            </template>
          </div>
          <div class="col-lg-3 col-sm-4 col-xl-2">
            <input-widget :model="model" attribute="date" type="datepicker" :min="$getMonthAgo(model.created_at)"
                          :max="$getMonthCurrent(model.created_at)"
                          :rules="`min_date:${$getMonthAgo(model.created_at)}|max_date:${$getMonthCurrent(model.created_at)}|required`"/>
          </div>
          <div class="col-lg-3 col-sm-4 col-xl-2" v-if="hasMoreThanOneLanguage">
            <language-code-select-input-widget hint :model="model" attribute="preferred_language_code"/>
          </div>
          <div class="col-lg-3 col-sm-4 col-xl-2">
            <input-widget :model="model" attribute="complaint_status" type="select2" :items="invoiceComplaintStatuses"
                          value-field="value" display-field="${item.key}"
                          :options="{placeholder: $t('Complaint Status')}"/>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-md-4">
            <div class="card">
              <div class="card-header d-flex align-items-center p-2">
                <div>
                  {{ $t('Customer Info') }}
                </div>
              </div>
              <div class="card-body p-2" style="min-height: 5.3em">
                <div v-if="customer || model.customer">
                  <customer-email-address-info :customer="customer ? customer : model.customer"
                                               show-intra-community-vat-number
                                               :show-address="false"/>
                </div>
                <no-data-available v-else v-show="true" height="4"/>
              </div>
            </div>
          </div>
          <div class="col-md-4" v-if="!model.is_pickup">
            <div class="card">
              <div class="card-header d-flex align-items-center p-2">
                <div>
                  {{ $t('Invoice Shipping address') }}
                </div>
                <b-button class="ml-auto p-0" variant="link" v-permission="'change_invoice'"
                          @click="onShippingAddressEditClick">
                  <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                  {{ $t('Edit') }}
                </b-button>
              </div>
              <div class="card-body p-2" style="min-height: 5.3em">
                <div v-if="model.invoice_shipping_address">
                  <p v-html="$options.filters.asAddress(model.invoice_shipping_address, false)" class="p-0"></p>
                </div>
                <no-data-available v-else v-show="true" height="4"/>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card">
              <div class="card-header d-flex align-items-center p-2">
                <div>
                  {{ $t('Invoice Billing address') }}
                </div>
                <b-button class="ml-auto p-0" variant="link" v-permission="'change_invoice'"
                          @click="onBillingAddressEditClick">
                  <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                  {{ $t('Edit') }}
                </b-button>
              </div>
              <div class="card-body p-2" style="min-height: 5.3em">
                <div v-if="model.invoice_billing_address">
                  <p v-html="$options.filters.asAddress(model.invoice_billing_address, false)" class="p-0"></p>
                </div>
                <no-data-available v-else v-show="true" height="4"/>
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-md-auto" v-if="!isAvalaraEnabled">
            <input-widget @change="calculateData()" is-switch attribute="is_exported"
                          :model="model" type="checkbox"/>
          </div>
          <div class="col-md-auto" v-if="!isAvalaraEnabled">
            <input-widget @change="calculateData()" is-switch
                          attribute="is_registered_business"
                          :model="model" type="checkbox"/>
          </div>
          <div class="col-md-auto" v-if="!isAvalaraEnabled">
            <input-widget @change="calculateData()" is-switch
                          attribute="is_tax_applied"
                          :model="model" type="checkbox"/>
          </div>
          <template v-if="showInvoiceExtraOptions && showInvoiceExtraOptions.length > 0 && !isAvalaraEnabled">
            <div class="col-md-auto" v-for="input in showInvoiceExtraOptions"
                 :key="`invoice-modal-dynamic-checkboxes-${input.key}`">
              <input-widget @change="calculateData()" is-switch :attribute="input.key" :model="model"
                            type="checkbox" :label="input.label"/>
            </div>
          </template>
          <div class="col-md-auto">
            <input-widget @change="calculateData()" is-switch
                          attribute="is_pickup"
                          :model="model" type="checkbox"/>
          </div>
          <div class="col-md-auto" v-permission="'change_customerbankaccount'" v-if="hasStripeProvider">
            <input-widget is-switch
                          attribute="exclude_from_auto_payments"
                          :model="model" type="checkbox"/>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-md-12">
            <b-button class="p-0 mb-2" variant="link" @click="onNewFinancialItemClick">
              <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
              {{ $t('Add New Inventory Item') }}
            </b-button>
          </div>
          <div class="col-md-12">
            <table class="info-table table table-sm lots-table table-responsive">
              <tr>
                <th style="min-width: 220px">{{ $t('Stock No') }}</th>
                <th style="min-width: 120px">{{ $t('Quantity') }}</th>
                <th style="min-width: 180px">{{ $t('Hammer Price') }}</th>
                <th style="min-width: 180px">{{ $t('Premium') }}</th>
                <th style="min-width: 150px" class="text-center">{{ $t('Actions') }}</th>
              </tr>
              <template v-if="model.financial_items.length > 0">
                <tr :key="`financial-items-${index}`" v-for="(item, index) in model.financial_items">
                  <td class="pr-4">
                    <inventory-select-input-widget with-image :model="item" attribute="inventory_uuid" :label="false"
                                                   size="sm" expand="inventory_miscellaneous_items"
                                                   @select="onInventorySelect"/>
                  </td>
                  <td class="pr-4">
                    <input-widget :model="item" attribute="quantity" :label="false" type="number" min="0"/>
                  </td>
                  <td class="pr-4">
                    <currency-input-widget @input="onHammerPriceChange(item)" :model="item" attribute="hammer_price"
                                           :label="false" :currency="currency"/>
                  </td>
                  <td class="pr-4">
                    <currency-input-widget @input="calculateData()" :model="item" attribute="premium" :label="false" :currency="currency"/>
                  </td>
                  <td class="text-center">
                    <b-tooltip :target="`remove-financial-item-button-${index}`" :title="$t('Delete')"/>
                    <font-awesome-icon class="text-danger hover-cursor" :id="`remove-financial-item-button-${index}`"
                                       @click="onFinancialItemDelete(index)" style="font-size: 1.5em;margin-top: .4em"
                                       :icon="'times'"/>
                  </td>
                </tr>
              </template>
              <tr v-else>
                <td :colspan="5">
                  <no-data-available :height="3"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-4 d-flex">
            <h5>{{ $t('Shipping') }}</h5>
            <input-widget @change="onShippingCheckboxChange" :model="model" attribute="enable_shipping" type="checkbox"
                          is-switch
                          :label="false" class="ml-3"/>
          </div>
          <div class="col-md-12">
            <table class="info-table table table-sm lots-table">
              <tr>
                <th style="width: 200px">{{ $t('Amount') }}</th>
                <th style="width: 200px">{{ $t('Tracking Code') }}</th>
                <th style="width: 15%" v-if="!isAvalaraEnabled">{{ $t('Taxable') }}</th>
                <th style="width: 30%">{{ $t('Calculate Shipping Automatically') }}</th>
              </tr>
              <tr>
                <td class="pr-4">
                  <currency-input-widget @input="calculateData()" :model="model" attribute="shipping_amount"
                                         type="number" :label="false" :currency="currency"
                                         :disabled="!model.enable_shipping || model.shipping_auto_calculation"/>
                </td>
                <td class="pr-4">
                  <input-widget :disabled="!model.enable_shipping"
                                :model="model" attribute="shipping_tracking_code" :label="false"/>
                </td>
                <td v-if="!isAvalaraEnabled">
                  <input-widget @change="calculateData()" :model="model" attribute="shipping_taxable" type="checkbox"
                                :label="false" :disabled="!model.enable_shipping || model.shipping_auto_calculation"/>
                </td>
                <td>
                  <input-widget @change="calculateData(false,true)" :model="model" attribute="shipping_auto_calculation"
                                type="checkbox"
                                :label="false" :disabled="!model.enable_shipping"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-4 d-flex">
            <h5>{{ $t('Comment') }}</h5>
          </div>
          <div class="col-md-12">
            <input-widget :model="model" attribute="comment" type="textarea" label=""/>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-md-12">
            <b-button class="p-0 mb-2" variant="link" @click="onNewMiscellaneousItemClick">
              <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
              {{ $t('Add Miscellaneous Charge') }}
            </b-button>
          </div>
          <div class="col-md-12" v-if="model.miscellaneous_items.length > 0">
            <table class="info-table table table-sm miscellaneous-table">
              <tr>
                <th style="width: 25%">{{ $t('Description') }}</th>
                <th style="width: 25%">{{ $t('Comment') }}</th>
                <th style="width: 20%">{{ $t('Amount') }}</th>
                <th style="width: 20%">{{ $t('Percentage') }}</th>
                <th style="width: 15%" class="text-center" v-if="!isAvalaraEnabled">{{ $t('Taxable') }}</th>
                <th style="width: 15%" class="text-center">{{ $t('Actions') }}</th>
              </tr>
              <tr :key="`miscellaneous-item-${index}`" v-for="(item, index) in model.miscellaneous_items">
                <td class="d-flex pr-3">
                  <input-widget v-if="hasPredefinedMiscCharges" style="flex: 1" :items="invoiceMiscellaneousItemCharges"
                                value-field="key"
                                display-field="${item.value}"
                                @select2Select="onMiscChargeDescriptionDropdownValueChange(item)"
                                type="select2" :model="item" :label="false" attribute="title_dropdown_value"
                                :rules="item.amount ? 'required' : ''"/>
                  <input-widget v-if="item.title_dropdown_value === 'other' || !hasPredefinedMiscCharges"
                                style="flex: 1"
                                :class="{'ml-2': hasPredefinedMiscCharges}"
                                :rules="item.amount ? 'required|max:255' : 'max:255'"
                                :model="item" attribute="title" :label="false"/>
                </td>
                <td class="pr-3">
                  <input-widget value-field="key"
                                display-field="${item.value}"
                                type="text" :model="item" :label="false" attribute="comment"/>
                </td>
                <td class="pr-3">
                  <currency-input-widget :rules="item.title ? 'required' : ''" :model="item" :currency="currency"
                                         @input="calculateData()" attribute="amount" :label="false"/>
                </td>
                <td>
                  <percentage-input-widget :model="item" :disabled="!item.inventory_uuid"
                                           @input="onMiscItemPercentageChange(item)" attribute="percentage"
                                           :label="false"/>
                </td>
                <td class="text-center" v-if="!isAvalaraEnabled">
                  <input-widget @change="calculateData()" attribute="taxable" :model="item" type="checkbox"
                                :label="false"/>
                </td>
                <td class="text-center">
                  <b-tooltip :target="`remove-commission-button-${index}`" :title="$t('Delete')"/>
                  <font-awesome-icon class="text-danger hover-cursor" :id="`remove-commission-button-${index}`"
                                     @click="onMiscellaneousItemDelete(index)" style="font-size: 1.5em;"
                                     :icon="'times'"/>
                </td>
              </tr>
            </table>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-md-5 offset-7">
            <invoice-totals-table :financial-items="model.financial_items" :model="model"
                                  :hide-shipping-vat="inclusiveTaxForFinancialObjects" :currency="currency"
                                  :hide-misc-charge-vat="inclusiveTaxForFinancialObjects"
                                  :skip-misc-charge-vat-from-total="inclusiveTaxForFinancialObjects"
                                  :skip-shipping-vat-from-total="inclusiveTaxForFinancialObjects"
                                  :miscellaneous-items="model.miscellaneous_items"/>
          </div>
        </div>
      </b-form>

      <template v-slot:modal-footer>
        <div class="d-flex align-items-center mr-auto text-muted" v-if="isRecalculating">
          <b-spinner class="mr-2"/>
          <h4 class="d-inline-block mb-0">
            {{ $t('Calculating') }}
          </h4>
        </div>
        <cancel-button @click="hideManualInvoiceModal" :disabled="loading">
          {{ $t('Cancel') }}
        </cancel-button>
        <submit-button @click="handleSubmit(onSubmit)" :show-loader="loading"
                       :disabled="model.financial_items.length === 0">
          {{ $t('Submit') }}
        </submit-button>
      </template>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import CancelButton from "../../core/components/common/CancelButton";
  import SubmitButton from "../../core/components/common/SubmitButton";
  import InvoicesService from "./InvoicesService";
  import {ON_PDF_MODAL_OPEN, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_CLOSE} from "@/plugins/event-bus/event-bus-names";
  import {SOURCE_INVOICES_TABLE} from "../constants";
  import NoDataAvailable from "../../core/components/common/NoDataAvailable";
  import CurrencyInputWidget from "../../core/widgets/CurrencyInputWidget";
  import MiscellaneousItem from "./models/MiscellaneousItem.model";
  import ManualInvoiceModel from "@/modules/invoices/models/ManualInvoice.model";
  import FinancialItemModel from "@/modules/invoices/models/FinancialItem.model";
  import InventorySelectInputWidget from "@/core/widgets/InventorySelectInputWidget";
  import CustomerSelectInputWidget from "@/core/widgets/CustomerSelectInputWidget";
  import _ from "lodash";
  import InvoiceTotalsTable from "@/modules/invoices/InvoiceTotalsTable";
  import {uuid} from "vue-uuid";
  import LanguageCodeSelectInputWidget from "@/core/widgets/LanguageCodeSelectInputWidget";
  import PercentageInputWidget from "../../core/widgets/PercentageInputWidget";
  import CustomerEmailAddressInfo from "../../core/components/customer-info/CustomerEmailAddressInfo";
  import {InvoiceModalMixin} from "@/modules/invoices/modals/invoiceModal.mixin";
  import LotsService from "@/modules/lots/LotsService";
  import helperService from "@/core/services/helper.service";
  import uuidV4 from 'uuid';
  import {hasPermission} from "@/core/directives/permission";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {
    mapState: mapStateGlobal,
    mapGetters: globalMapGetters,
    mapActions: globalMapActions
  } = createNamespacedHelpers('global');

  export default {
    name: "ManualInvoiceModal",
    mixins: [InvoiceModalMixin],
    components: {
      PercentageInputWidget,
      LanguageCodeSelectInputWidget,
      InvoiceTotalsTable,
      CustomerSelectInputWidget,
      InventorySelectInputWidget,
      CustomerEmailAddressInfo,
      CurrencyInputWidget,
      NoDataAvailable,
      SubmitButton,
      CancelButton,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        modalUuid: uuidV4(),
        customerUuid: null,
        loading: false,
        showConfirmation: true,
        model: new ManualInvoiceModel(),
        isRecalculating: false,
      }
    },
    computed: {
      ...mapStateGlobal([
        'hasMoreThanOneLanguage',
        'invoiceComplaintStatuses',
        'inclusiveTaxForFinancialObjects',
        'currentBranch',
        'showInvoiceExtraOptions',
        'shippingConfigEnabled',
        'dynamicTexts',
        'hasStripeProvider'
      ]),
      ...mapStateGlobal({
        invoiceMiscellaneousItemCharges: state => state.invoiceMiscellaneousItemCharges,
      }),
      ...globalMapGetters([
        'isAvalaraEnabled'
      ]),
      ...mapState({
        modal: state => state.manualInvoiceModal,
        singleInventoryItem: state => state.manualInvoiceModal.singleInventoryItem,
        customer: state => state.manualInvoiceModal.customer,
        editObject: state => state.manualInvoiceModal.object,
      }),
      computedTitle() {
        if (this.modal.creditNoteWithoutRefundParams) {
          return this.$i18n.t('Recreate Invoice {no}', {no: this.model.invoice_no})
        }
        if (!this.model || !this.model.uuid) {
          return this.$i18n.t('Generate Manual Invoice')
        }
        return this.$i18n.t('Update Invoice {no}', {no: this.model.invoice_no})
      },
      hasPredefinedMiscCharges() {
        return this.invoiceMiscellaneousItemCharges && this.invoiceMiscellaneousItemCharges.length > 1;
      },
      currency() {
        if (this.model && this.model.currency) {
          return this.model.currency.symbol
        }
        return null
      }
    },
    watch: {
      async customer() {
        this.$nextTick(function () {
          if (this.customer) {
            this.model.customer_uuid = this.customer.uuid;
            if (!this.model.uuid) {
              this.setDefaultAddresses(this.customer.billing_address, this.customer.shipping_address)
              this.customerUuid = this.customer.uuid
              if (this.customer.term) {
                this.model.is_pickup = this.customer.term.is_pickup;
                if (!this.model.uuid && hasPermission('change_customerbankaccount') && this.customer.term && this.hasStripeProvider) {
                  this.model.exclude_from_auto_payments = this.customer.term.exclude_from_auto_payments;
                }
              }

            }
          }
        })
      },
      async editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.model = new ManualInvoiceModel(this.editObject);
          let cfc = this.currentBranch.tax_rate / 100 + 1

          if (this.model.shipping_taxable && this.inclusiveTaxForFinancialObjects) {
            this.model.shipping_amount = Math.round(parseFloat(this.model.shipping_amount * cfc));
          }

          this.model.financial_items = this.model.financial_items.map(item => new FinancialItemModel(item));
          this.model.miscellaneous_items = this.model.miscellaneous_items.map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              item.amount = Math.round(parseFloat(item.amount * cfc))
            }
            return new MiscellaneousItem(item)
          });

          if (this.editObject.customer) {
            this.customerUuid = this.editObject.customer.uuid
            if (!this.model.preferred_language_code) {
              this.model.preferred_language_code = this.editObject.customer.preferred_language_code
            }
          }

          this.setDefaultAddresses(
            this.editObject.invoice_billing_address,
            this.editObject.invoice_shipping_address,
          )

          // Enable_shipping value is not saved in database, so it needs to be set here
          this.model.enable_shipping = !!this.model.shipping_amount;
        }
      },
      singleInventoryItem() {
        if (this.singleInventoryItem) {
          this.model.financial_items.push(new FinancialItemModel({
            uuid: uuid.v4(),
            is_create: true,
            inventory_uuid: this.singleInventoryItem.uuid,
            inventory: this.singleInventoryItem
          }))
        }
      }
    },
    methods: {
      ...mapActions(['hideManualInvoiceModal', 'getTableData']),
      ...globalMapActions(['showAddressModal']),
      async onShow() {
        this.showConfirmation = true;
        this.loading = false;
      },
      onHidden() {
        this.customerUuid = null
        this.model = new ManualInvoiceModel();
        this.hideManualInvoiceModal();
      },
      onShippingCheckboxChange() {
        this.$nextTick(() => {
          if (!this.model.enable_shipping) {
            this.model.shipping_tracking_code = null;
            this.model.shipping_amount = null;
            this.model.shipping_taxable = false;
            this.model.shipping_auto_calculation = false;
          } else {
            if (this.shippingConfigEnabled) {
              this.model.shipping_auto_calculation = true;
            }
          }
          this.calculateData(false, true);
        })
      },
      async onSubmit() {
        if (this.model.is_not_editable) {
          this.$errorDialog(this.$i18n.t('You can not edit legacy invoice'));
          return;
        }
        this.model.resetErrors();

        this.loading = true;
        let response;

        const reservation_proposals = (await LotsService.getLatestReservationProposals({
          inventory_uuids:
            this.model.financial_items.map(item => item.inventory_uuid)
        })).body
        if (reservation_proposals.length) {
          const confirm = await helperService.showProposalsConfirmationModal(reservation_proposals, this.$confirm, this.$router, this.$options.filters)
          if (!confirm) {
            this.loading = false
            return
          }
        }

        let unpaidInvoiceNotificationSuccess = false;
        if (!this.model.uuid || this.modal.creditNoteWithoutRefundParams) {
          unpaidInvoiceNotificationSuccess = await this.$confirm(
            "Would you like to send `New Invoice Notification` to the customer?"
          );
        }

        let cfc = this.currentBranch.tax_rate / 100 + 1

        let shippingAmount = this.model.shipping_amount
        if (this.inclusiveTaxForFinancialObjects && this.model.enable_shipping && this.model.shipping_taxable && shippingAmount) {
          shippingAmount = parseFloat(shippingAmount / cfc).toFixed(2)
        }

        let extra_info = {}
        this.showInvoiceExtraOptions.forEach(i => extra_info[i.key] = this.model[i.key])

        const requestData = {
          ...this.model.toJSON(),
          type: 'manual',
          invoice_tax_rate: this.model.tax_rate,
          financial_items: this.model.financial_items.map(item => {
            if (item.exclude_tax_for_premium === null) {
              item.exclude_tax_for_premium = false;
            }
            if (item.exclude_tax_for_hammer_price === null) {
              item.exclude_tax_for_hammer_price = false;
            }
            return item.toJSON();
          }),
          miscellaneous_items: this.model.miscellaneous_items.filter(item => (item.title || item.title_dropdown_value) && item.amount).map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              item.amount = parseFloat(item.amount / cfc).toFixed(2)
            }
            if (this.modal.creditNoteWithoutRefundParams) {
              delete item.uuid;
            }
            return item
          }),
          shipping_amount: this.model.enable_shipping ? shippingAmount : null,
          shipping_taxable: this.model.enable_shipping ? this.model.shipping_taxable : false,
          complaint_status: this.model.complaint_status || null,
          send_unpaid_invoice_notification: unpaidInvoiceNotificationSuccess,
          shipping_vat: null,
          comment: this.model.comment,
          date: this.model.date,
          extra_info
        };

        if (hasPermission('change_customerbankaccount') && this.hasStripeProvider) {
          requestData['exclude_from_auto_payments'] = this.model.exclude_from_auto_payments;
        }

        if (this.modal.creditNoteWithoutRefundParams) {
          requestData.inherited_from_uuid = requestData.uuid;
          requestData.uuid = null;
          requestData.credit_note_without_refund_params = this.modal.creditNoteWithoutRefundParams;
          response = await InvoicesService.createInvoice(requestData);
        } else if (!this.model.uuid) {
          response = await InvoicesService.createInvoice(requestData);
        } else {
          response = await InvoicesService.updateInvoice(requestData.uuid, requestData);
        }
        this.loading = false;
        if (response.success) {
          this.showConfirmation = false;

          if (this.modal.source === SOURCE_INVOICES_TABLE) {
            this.getTableData();
          }

          if (!this.model.uuid && this.$hasPermission('export_invoice')) {
            const invoice = response.body.invoice;
            this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
              title: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: invoice.invoice_no}),
            });
            let pdfDataResponse = await InvoicesService.getInvoicePdfData(invoice.uuid);
            if (pdfDataResponse.success) {
              this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
                ...pdfDataResponse.body,
                filename: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: invoice.invoice_no})
              });
            } else {
              let msg = this.$i18n.t("An error occurred during PDF generation");
              if (pdfDataResponse.body && pdfDataResponse.body.non_field_errors) {
                msg = pdfDataResponse.body.non_field_errors
              }
              this.$errorDialog(msg);
              this.$evBus.$emit(ON_PDF_MODAL_CLOSE);
            }
          }
          this.hideManualInvoiceModal();
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            if (response.body && typeof response.body === 'object') {
              this.model.setMultipleErrors(response.body);
            } else {
              this.$errorDialog(this.$i18n.t("An error occurred during invoice generation."));
            }
          }
        }
      },
      onFinancialItemDelete(index) {
        for (let i = 0; i < this.model.financial_items.length; i++) {
          if (i === index) {
            this.model.financial_items.splice(i, 1);
            break;
          }
        }
        this.calculateData(false)
      },
      onNewFinancialItemClick() {
        let financialItemData = {uuid: uuid.v4(), is_create: true};
        financialItemData['quantity'] = 1;
        this.model.financial_items.push(new FinancialItemModel(financialItemData));
      },
      calculateData: _.debounce(async function (recalculatePremium = false, calculateShipping = false) {
        const isFormValid = await this.$refs.observer.validate();
        if (!isFormValid) {
          return;
        }
        this.isRecalculating = true;

        let cfc = this.currentBranch.tax_rate / 100 + 1

        let shippingAmount = this.model.shipping_amount
        if (this.inclusiveTaxForFinancialObjects && this.model.enable_shipping && this.model.shipping_taxable && shippingAmount) {
          shippingAmount = parseFloat(shippingAmount / cfc).toFixed(2)
        }

        let extra_info = {}
        this.showInvoiceExtraOptions.forEach(i => extra_info[i.key] = this.model[i.key])

        let {success, body} = await InvoicesService.calculateData({
          uuid: this.model.uuid,
          shipping_auto_calculation: this.model.shipping_auto_calculation,
          invoice_billing_address: this.model.invoice_billing_address,
          invoice_shipping_address: this.model.invoice_shipping_address,
          customer_uuid: this.model.customer ? this.model.customer.uuid : null,
          invoice_tax_rate: this.model.tax_rate,
          financial_items: this.model.financial_items.filter(item => item.inventory_uuid).map(item => {
            return {
              uuid: item.uuid,
              quantity: item.quantity,
              premium: recalculatePremium ? null : parseFloat(item.premium),
              hammer_price: parseFloat(item.hammer_price),
              inventory_uuid: item.inventory_uuid,
              exclude_tax_for_premium: item.exclude_tax_for_premium,
              exclude_tax_for_hammer_price: item.exclude_tax_for_hammer_price,
            }
          }),
          miscellaneous_items: this.model.miscellaneous_items.filter(item => (item.title || item.title_dropdown_value) && item.amount).map(item => {
            if (item.taxable && this.inclusiveTaxForFinancialObjects) {
              item.amount = parseFloat(item.amount / cfc).toFixed(2)
            }
            return item
          }),
          is_exported: this.model.is_exported,
          is_tax_applied: this.model.is_tax_applied,
          is_registered_business: this.model.is_registered_business,
          is_pickup: this.model.is_pickup,
          shipping_amount: this.model.enable_shipping ? shippingAmount : null,
          shipping_taxable: this.model.enable_shipping ? this.model.shipping_taxable : false,
          type: 'manual',
          extra_info,
          invoiced_financial_item_uuids: this.model.financial_items.map(item => item.uuid),
        }, 'miscellaneous_items,financial_items,buyer_financial_item_charges.charge,unsaved_buyer_financial_item_charges.charge,buyer_financial_item_charges_to_delete.charge,vendor_financial_item_charges_to_delete.charge');

        if (success) {
          if (body.financial_items.length > 0) {
            for (let financialItem of body.financial_items) {
              let itemIndex = this.model.financial_items.findIndex(i => i.uuid === financialItem.uuid);
              if (itemIndex >= 0) {
                this.model.financial_items[itemIndex] = new FinancialItemModel({
                  ...this.model.financial_items[itemIndex],
                  ...financialItem
                });
              }
            }
            this.model.financial_items = [...this.model.financial_items];
          }

          if (body.miscellaneous_items.length > 0) {
            for (let miscItem of body.miscellaneous_items) {
              let itemIndex;
              if (miscItem.uuid) {
                itemIndex = this.model.miscellaneous_items.findIndex(mi => mi.uuid === miscItem.uuid);
              } else {
                itemIndex = this.model.miscellaneous_items.findIndex(mi =>
                  (mi.title === miscItem.title || mi.title_dropdown_value === miscItem.title) && mi.amount == miscItem.amount
                );
              }

              if (itemIndex >= 0) {

                if (miscItem.taxable && this.inclusiveTaxForFinancialObjects) {
                  miscItem.amount = Math.round(parseFloat(miscItem.amount * cfc))
                }

                if (miscItem.uuid) {
                  this.model.miscellaneous_items[itemIndex] = new MiscellaneousItem({
                    ...this.model.miscellaneous_items[itemIndex],
                    ...miscItem
                  })
                } else {
                  this.model.miscellaneous_items[itemIndex] = new MiscellaneousItem({
                    ...this.model.miscellaneous_items[itemIndex],
                    amount: miscItem.amount,
                    vat: miscItem.vat,
                    taxable: miscItem.taxable,
                    title: miscItem.title,
                  }, true);
                }
              }
            }
            this.model.miscellaneous_items = [...this.model.miscellaneous_items];
          }

          this.model.tax_rate = body.tax_rate;
          if (body.shipping_taxable && this.inclusiveTaxForFinancialObjects) {
            this.model.shipping_amount = Math.round(parseFloat(body.shipping_amount * cfc));
          } else {
            this.model.shipping_amount = body.shipping_amount;
          }
          this.model.shipping_vat = body.shipping_vat;
          this.model.shipping_taxable = body.shipping_taxable;
          if (!calculateShipping) {
            this.model.enable_shipping = !!this.model.shipping_amount;
          }
        } else {
          if (body.non_field_errors) {
            this.$errorDialog(body.non_field_errors[0]);
          } else {
            this.model.setMultipleErrors(body);
          }
        }

        this.isRecalculating = false;
      }, 500),
      onHammerPriceChange: _.debounce(async function (financialItem) {
        this.calculateMiscChargesWithPercent(financialItem.inventory_uuid)
        await this.calculateData(true)
      }, 500),
      onMiscItemPercentageChange: _.debounce(async function (miscItem) {
        this.calculateMiscChargesWithPercent(miscItem.inventory_uuid)
        await this.calculateData()
      }, 500),
      onCustomerSelect(customer) {
        this.model.customer = customer;
        this.customerUuid = customer.uuid
        this.model.preferred_language_code = customer.preferred_language_code;
        this.model.is_pickup = customer.term.is_pickup;
        this.setDefaultAddresses(customer.billing_address, customer.shipping_address)
        if (!this.model.uuid && hasPermission('change_customerbankaccount') && customer.term && this.hasStripeProvider) {
          this.model.exclude_from_auto_payments = customer.term.exclude_from_auto_payments;
        }
        this.$nextTick(() => {
          this.calculateData();
        })
      },
      onCustomerUnselect() {
        this.model.invoice_shipping_address = null
        this.model.invoice_billing_address = null
        this.$nextTick(() => {
          this.calculateData();
        })
      },
      setTaxTerms(taxTerms) {
        if (taxTerms && Object.keys(taxTerms).length > 0) {
          this.model.financial_items.forEach(item => {
            item.exclude_tax_for_premium = taxTerms.exclude_tax_for_premium;
          });
          if (taxTerms.is_economic_nexus_threshold_reached) {
            if (this.model.enable_shipping) {
              this.model.shipping_taxable = true;
            }
            if (this.model.miscellaneous_items.length > 0) {
              this.model.miscellaneous_items.forEach(item => {
                item.taxable = true;
              })
            }
          }
        }
      },

      onInventorySelect(item) {
        if (item.inventory_miscellaneous_items && item.inventory_miscellaneous_items.length > 0) {
          for (let miscItem of item.inventory_miscellaneous_items) {
            this.model.miscellaneous_items.push(new MiscellaneousItem({
              amount: miscItem.amount,
              title: miscItem.title || miscItem.description,
              comment: miscItem.comment,
              taxable: miscItem.taxable,
              percentage: miscItem.percentage,
              inventory_uuid: miscItem.inventory_uuid,
            }, true))
          }
        }
      },
      calculateMiscChargesWithPercent(inventoryUuid) {
        let fi = this.model.financial_items.find(fi => fi.inventory_uuid === inventoryUuid)
        if (!fi || !fi.inventory_uuid) {
          return
        }

        for (let i in this.model.miscellaneous_items) {
          let miscItem = this.model.miscellaneous_items[i]
          if (fi.inventory_uuid === miscItem.inventory_uuid && miscItem.percentage !== null && miscItem.percentage) {
            miscItem.amount = parseFloat(parseFloat(fi.hammer_price) * parseFloat((miscItem.percentage / 100))).toFixed(2)
          }
        }

      },

      onMiscChargeDescriptionDropdownValueChange(item) {
        item.title = '';
      },
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .items-table, .miscellaneous-table {
    /deep/ .form-group {
      margin-top: .2em;
      margin-bottom: .5em;
    }
  }

  .miscellaneous-table {
    .misc-item-row {
      td:nth-child(3), td:nth-child(4) {
        vertical-align: middle;
      }
    }
  }
</style>
